import { generateColors } from '@mantine/colors-generator';
import { createTheme } from '@mantine/core';

export const theme = createTheme({
  colors: {
    blue: generateColors('#07badd'),
    pastelBlue: generateColors('#d7faff'),
    yellow: generateColors('#fcb913'),
    navy: generateColors('#011639'),
    grey: generateColors('#393f47'),
    orange: generateColors('#ff8006'),
  },
  primaryColor: 'yellow', // Yellow for primary CTAs as per brand guidelines
  fontFamily: "'Montserrat', sans-serif",
  headings: {
    fontFamily: "'Montserrat', sans-serif",
    // Brand typography hierarchy
    sizes: {
      h1: { fontWeight: '600', fontSize: '2rem', lineHeight: '1.2' }, // Semibold for primary headings
      h2: { fontWeight: '500', fontSize: '1.75rem', lineHeight: '1.3' }, // Medium for secondary headings
      h3: { fontWeight: '500', fontSize: '1.5rem', lineHeight: '1.4' },
      h4: { fontWeight: '500', fontSize: '1.25rem', lineHeight: '1.4' },
      h5: { fontWeight: '400', fontSize: '1.125rem', lineHeight: '1.5' }, // Regular for smaller headings
      h6: { fontWeight: '400', fontSize: '1rem', lineHeight: '1.5' },
    },
  },
  // Brand spacing tokens
  spacing: {
    section: '24px', // 24px for sections
    card: '16px', // 16px for cards
    compact: '8px', // 8px for compact spacing
  },
  components: {
    Button: {
      defaultProps: {
        color: 'yellow', // Yellow for primary CTAs per brand guidelines
        radius: 'md',
        size: 'md',
      },
      styles: () => ({
        root: {
          // Primary CTA - Yellow background with navy text for filled variant
          '&[data-variant="filled"]': {
            backgroundColor: 'var(--mantine-color-yellow-5)',
            color: 'var(--mantine-color-navy-9)',
            border: 'none',
            '&:hover': {
              backgroundColor: 'var(--mantine-color-yellow-6)',
            },
            '&:active': {
              backgroundColor: 'var(--mantine-color-yellow-7)',
            },
          },
          // Secondary actions - Navy outline
          '&[data-variant="outline"]': {
            borderColor: 'var(--mantine-color-navy-5)',
            color: 'var(--mantine-color-navy-7)',
            '&:hover': {
              backgroundColor: 'var(--mantine-color-navy-0)',
              borderColor: 'var(--mantine-color-navy-6)',
            },
          },
        },
        label: {
          fontWeight: '600',
          textTransform: 'uppercase',
          color: 'var(--mantine-color-navy-9)',
        },
      }),
    },
    Text: {
      defaultProps: {
        fontFamily: "'Montserrat', sans-serif",
        fw: 400, // Regular weight for body text as per brand guidelines
      },
      styles: () => ({
        root: {
          color: 'var(--mantine-color-navy-9)',
          // Brand typography class variations
          '&[data-variant="metadata"]': {
            fontWeight: 300, // Light for metadata
            color: 'var(--mantine-color-grey-6)',
          },
          '&[data-variant="body"]': {
            fontWeight: 400, // Regular for body text
            color: 'var(--mantine-color-navy-8)',
          },
          '&[data-variant="emphasis"]': {
            fontWeight: 500, // Medium for emphasis
            color: 'var(--mantine-color-navy-9)',
          },
        },
      }),
    },
    Card: {
      defaultProps: {
        padding: 'var(--mantine-spacing-card)', // Use brand spacing token
        radius: 'md',
        shadow: 'sm',
      },
      styles: () => ({
        root: {
          borderColor: 'var(--mantine-color-navy-2)',
          backgroundColor: 'var(--mantine-color-white)',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: 'var(--mantine-color-yellow-4)',
            transform: 'translateY(-1px)',
            boxShadow: 'var(--mantine-shadow-md)',
          },
        },
      }),
    },
    Paper: {
      defaultProps: {
        padding: 'var(--mantine-spacing-section)', // Use brand spacing token
        radius: 'md',
        shadow: 'md',
      },
      styles: () => ({
        root: {
          backgroundColor: 'var(--mantine-color-white)',
        },
      }),
    },
  },
  other: {
    popupWidth: 320,
    popupHeight: 200,
    // Brand design tokens
    brandSpacing: {
      section: '24px',
      card: '16px',
      compact: '8px',
    },
    brandColors: {
      primaryNavy: '#011639',
      primaryYellow: '#fcb913',
      secondaryOrange: '#ff8006',
      secondaryGrey: '#393f47',
      secondaryBlue: '#07badd',
      pastelBlue: '#d7faff',
    },
  },
});
